/**
 * Flat-top axial-hex math + procedural biome/noise helpers.
 *
 * Shared by the deterministic simulation (pathfinding, grid indexing) and the
 * 3D renderer. The single source of truth for what a "hex" is, so sim and
 * view always agree on coordinates.
 */

export const SQRT3 = Math.sqrt(3);

export type Biome =
  | "deepWater"
  | "water"
  | "sand"
  | "desert"
  | "savanna"
  | "grass"
  | "forest"
  | "jungle"
  | "taiga"
  | "rock"
  | "mountain"
  | "snow";

export interface Hex {
  q: number; // axial q
  r: number; // axial r
  col: number; // odd-q offset column (storage index)
  row: number; // odd-q offset row
  x: number; // world-space center X
  z: number; // world-space center Z
  biome: Biome;
  height: number; // top-of-prism height (visual) — snapped to a discrete tier
  /** Integer elevation tier (0 = shore/plains, higher = uphill). Water = -1.
   *  Walkability is per-EDGE: adjacent hexes <= MAX_CLIMB tiers apart are
   *  traversable; a steeper face is a cliff you must route around. */
  level: number;
  /** 0..1 dirt-vs-grass tint for grass hexes (organic patchiness). */
  tint?: number;
}

// --- world scale (keep elevation human-proportioned) ----------------------
// A hex is ~10 m across (flat-to-flat = SQRT3*size world units). Everything
// vertical is derived from that so a soldier (~2 m) never scales a cliff three
// times his height: one elevation tier is ~1 m (a knee/waist step) and the most
// a unit can climb between two tiles is MAX_CLIMB tiers (~2 m, a real clamber).
// A face taller than that is a cliff — pathing routes around it.
export const METERS_PER_TILE = 10;
export const METERS_PER_TIER = 1;

/** World-space height of one elevation tier for a given hex size. */
export function tierHeight(size: number): number {
  return SQRT3 * size * (METERS_PER_TIER / METERS_PER_TILE);
}

/** Max elevation tiers a unit can step up/down between adjacent hexes. One tier
 *  ≈ 1 m (a knee/waist step); a jump of MAX_CLIMB+1 or more (≈2 m, chest-high on
 *  a unit) is a cliff — impassable directly, you walk around to a gentler spot.
 *  At 1 the map stays 100% reachable (measured) — just with believable detours. */
export const MAX_CLIMB = 1;

/** flat-top axial -> world (X, Z). */
export function hexToPixel(q: number, r: number, size: number): [number, number] {
  return [size * 1.5 * q, size * SQRT3 * (r + q / 2)];
}

/** odd-q offset -> axial. */
export function offsetToAxial(col: number, row: number): [number, number] {
  const q = col;
  const r = row - ((col - (col & 1)) >> 1);
  return [q, r];
}

/** axial -> odd-q offset (inverse of offsetToAxial). */
export function axialToOffset(q: number, r: number): [number, number] {
  const col = q;
  const row = r + ((q - (q & 1)) >> 1);
  return [col, row];
}

export const HEX_DIRECTIONS: [number, number][] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** 6 flat-top corners around a center (returns [x, z] pairs). */
export function hexCorners(cx: number, cz: number, size: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * 60 * i; // 0°, 60°, ... (flat-top)
    pts.push([cx + size * Math.cos(a), cz + size * Math.sin(a)]);
  }
  return pts;
}

/** Distance in hex steps between two axial coords. */
export function hexDistance(aq: number, ar: number, bq: number, br: number): number {
  return (Math.abs(aq - bq) + Math.abs(aq + ar - bq - br) + Math.abs(ar - br)) / 2;
}

/** Round fractional axial coords to the nearest integer hex (cube round). */
function cubeRound(qf: number, rf: number): [number, number] {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  let s = Math.round(sf);
  const qd = Math.abs(q - qf);
  const rd = Math.abs(r - rf);
  const sd = Math.abs(s - sf);
  if (qd > rd && qd > sd) q = -r - s;
  else if (rd > sd) r = -q - s;
  return [q, r];
}

/** World (X, Z) -> axial (q, r) of the nearest hex center (flat-top). */
export function pixelToAxial(x: number, z: number, size: number): [number, number] {
  const q = (2 / 3) * (x / size);
  const r = (-1 / 3) * (x / size) + (SQRT3 / 3) * (z / size);
  return cubeRound(q, r);
}

// ---- deterministic value noise (fBm) -------------------------------------

function hash2(x: number, y: number, seed: number): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0;
  h = (h ^ (h >> 13)) * 1274126177;
  h = h ^ (h >> 16);
  return (h >>> 0) / 4294967295;
}
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
function smooth(t: number) {
  return t * t * (3 - 2 * t);
}

export function makeNoise(seed: number) {
  const at = (x: number, y: number) => {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = smooth(x - x0);
    const fy = smooth(y - y0);
    const v00 = hash2(x0, y0, seed);
    const v10 = hash2(x0 + 1, y0, seed);
    const v01 = hash2(x0, y0 + 1, seed);
    const v11 = hash2(x0 + 1, y0 + 1, seed);
    return lerp(lerp(v00, v10, fx), lerp(v01, v11, fx), fy);
  };
  return (x: number, y: number, octaves = 4) => {
    let amp = 1;
    let sum = 0;
    let norm = 0;
    let f = 1;
    for (let o = 0; o < octaves; o++) {
      sum += at(x * f, y * f) * amp;
      norm += amp;
      amp *= 0.5;
      f *= 2;
    }
    return sum / norm; // 0..1
  };
}

export function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * FNV-1a 32-bit hash of an arbitrary string -> uint32. Lets a seed be a word
 * ("hello", "funky-banana") instead of just a number.
 */
export function hashSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface GenOptions {
  cols: number;
  rows: number;
  size: number;
  seed: number;
  /** Explicit absolute sea level (overrides seaFraction). Default: derived. */
  seaLevel?: number;
  /** Explicit absolute mountain level (overrides mountainFraction). Default: derived. */
  mountainLevel?: number;
  /** Fraction of hexes that should be water (sea). Default 0.28. */
  seaFraction?: number;
  /** Fraction of hexes that should be mountains (highest elevation). Default 0.12. */
  mountainFraction?: number;
  /** Fraction of LAND hexes that should be forest (by moisture). Default 0.40. */
  forestFraction?: number;
  /** biome-blob horizontal frequency (lower = larger regions). Default 3.0 */
  freqX?: number;
  /** biome-blob vertical frequency (lower = larger regions). Default 2.4 */
  freqY?: number;
  /** anti-speckle majority-filter passes. Default 1 */
  smoothPasses?: number;
  /** Height-relaxation passes for smooth rolling hills. Default 6. */
  heightSmoothPasses?: number;
  /** Extra height (in tiers) piled onto mountain/rock hexes AFTER smoothing, to
   *  carve steep, unclimbable flanks where the highlands meet lower ground.
   *  Default 0 (smoothed terraces only, no hard cliffs). */
  mountainLift?: number;
}

export function generateHexGrid(opts: GenOptions): Hex[] {
  const { cols, rows, size, seed } = opts;
  const ex = opts.freqX ?? 3.0;
  const ey = opts.freqY ?? 2.4;
  const elev = makeNoise(seed);
  const moist = makeNoise(seed + 777);
  const tintN = makeNoise(seed + 555);
  const tempN = makeNoise(seed + 999);

  interface Sample { q: number; r: number; col: number; row: number; x: number; z: number; e: number; m: number; t: number; tint: number; }
  const samples: Sample[] = [];
  const es: number[] = [];
  const ms: number[] = [];
  const ts: number[] = [];
  for (let col = 0; col < cols; col++) {
    for (let row = 0; row < rows; row++) {
      const [q, r] = offsetToAxial(col, row);
      const [x, z] = hexToPixel(q, r, size);
      const u = col / cols;
      const v = row / rows;
      const e = elev(u * ex, v * ey, 5);
      const m = moist(u * (ex + 0.5), v * (ey + 0.5), 4);
      const tint = tintN(u * 1.3, v * 1.1, 2);
      // temperature: warm at the equator (mid rows), cold toward the poles, with
      // softer noise blobs for regional climate + cooling at higher elevation —
      // a coarse Whittaker climate so biomes form coherent latitude-ish bands.
      const lat = 1 - Math.abs(v - 0.5) * 2; // 1 equator → 0 poles
      const t = lat * 0.6 + tempN(u * 1.6, v * 1.4, 3) * 0.4 - e * 0.25;
      samples.push({ q, r, col, row, x, z, e, m, t, tint });
      es.push(e);
      ms.push(m);
      ts.push(t);
    }
  }

  const seaFrac = opts.seaFraction ?? 0.28;
  const mtnFrac = opts.mountainFraction ?? 0.12;
  const forestFrac = opts.forestFraction ?? 0.40;
  const seaT = opts.seaLevel ?? quantile(es, seaFrac);
  const mtnT = opts.mountainLevel ?? quantile(es, 1 - mtnFrac);
  // Beach (sand) and rock are QUANTILE bands, not fixed elevation widths.
  // Fixed widths (the old seaT+0.03 / mtnT-0.05) silently swallowed the whole
  // grass/forest middle whenever the elevation spread was narrow — e.g. seed
  // 1337 produced 0% forest. Quantile bands keep the grass/forest middle a
  // stable ~45% of the map for every seed.
  const deepT = quantile(es, seaFrac * 0.5);
  const sandT = quantile(es, Math.min(seaFrac + 0.05, 0.5));
  const rockT = quantile(es, Math.max(1 - mtnFrac - 0.1, 0.5));
  const forestT = quantile(ms, forestFrac);
  // climate band cut-offs (quantiles → stable proportions per seed)
  const coldT = quantile(ts, 0.34);
  const hotT = quantile(ts, 0.70);
  const aridT = quantile(ms, 0.20); // below this = true desert dryness
  let eMax = -Infinity;
  for (const v of es) if (v > eMax) eMax = v;

  const hexes: Hex[] = [];
  for (const s of samples) {
    const biome = classifyBiome(s.e, s.m, s.t, { deepT, seaT, sandT, rockT, mtnT, forestT, coldT, hotT, aridT });
    const height = heightFor(s.e, seaT, eMax);
    hexes.push({ q: s.q, r: s.r, col: s.col, row: s.row, x: s.x, z: s.z, biome, height, level: 0, tint: s.tint });
  }

  const passes = opts.smoothPasses ?? 1;
  if (passes > 0) smoothBiomes(hexes, passes);
  const hpasses = opts.heightSmoothPasses ?? 6;
  if (hpasses > 0) smoothHeights(hexes, hpasses);
  quantizeElevation(hexes, size, opts.mountainLift ?? 0);
  return hexes;
}

/**
 * Snap the smooth height field into discrete elevation TIERS. After this each
 * land hex sits at `shore + level * step` (flat top), so the per-hex prisms the
 * renderer already draws become terraces with vertical cliff faces between
 * different levels. Walkability then keys off the integer `level` difference
 * between neighbours (see MAX_CLIMB) — what you see (a tall face) is exactly
 * what blocks. Water keeps its sea-floor height and a sentinel level of -1.
 */
function quantizeElevation(hexes: Hex[], size: number, mountainLift: number): void {
  const shore = 0.18; // land floor (matches heightFor)
  const step = tierHeight(size); // ~1 m per tier, human-scale
  for (const h of hexes) {
    if (h.biome === "water" || h.biome === "deepWater") { h.level = -1; continue; }
    let lvl = Math.max(0, Math.round((h.height - shore) / step));
    // Pile extra tiers onto the highlands so their flanks become steep cliffs:
    // mountains rise `mountainLift` tiers, the rock ring half that — so you climb
    // grass -> rock -> mountain via gentle (<= MAX_CLIMB) ramps where rock laps
    // the range, but a spot where mountain drops straight to low ground is an
    // unclimbable face you must walk around.
    if (mountainLift > 0) {
      if (h.biome === "mountain") lvl += mountainLift;
      else if (h.biome === "rock" || h.biome === "snow") lvl += Math.floor(mountainLift / 2);
    }
    h.level = lvl;
    h.height = shore + lvl * step;
  }
}

/** Percentile (0..1) of a numeric array via sorting. */
function quantile(values: number[], frac: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const i = Math.min(sorted.length - 1, Math.max(0, Math.floor(frac * (sorted.length - 1))));
  return sorted[i];
}

/** Smooth, continuous terrain height from elevation. Water is a flat sea floor. */
export function heightFor(e: number, seaT: number, eMax: number): number {
  const shore = 0.18;
  const seaFloor = -0.2;
  if (e < seaT) return seaFloor;
  const le = (e - seaT) / Math.max(1e-4, eMax - seaT);
  return shore + Math.pow(le, 1.7) * 1.8;
}

interface BiomeThresholds {
  deepT: number;
  seaT: number;
  sandT: number;
  rockT: number;
  mtnT: number;
  forestT: number;
  coldT: number;
  hotT: number;
  aridT: number;
}

/** Biome drives COLOR + props only (not height). Elevation picks the broad band
 *  (sea / beach / lowland / rock / peak); within the lowland band, temperature
 *  (t) and moisture (m) pick the climate flavour — a coarse Whittaker diagram:
 *
 *        dry            humid
 *   cold  grass/tundra  taiga
 *   warm  grass         forest
 *   hot   desert/savanna jungle
 */
function classifyBiome(e: number, m: number, t: number, T: BiomeThresholds): Biome {
  if (e < T.deepT) return "deepWater";
  if (e < T.seaT) return "water";
  if (e < T.sandT) return "sand";
  // peaks are snow-capped unless they sit in hot latitudes; cold high rock too
  if (e > T.mtnT) return t < T.hotT ? "snow" : "mountain";
  if (e > T.rockT) return t < T.coldT ? "snow" : "rock";
  const wet = m > T.forestT;
  if (t < T.coldT) return wet ? "taiga" : "grass";
  if (t > T.hotT) return wet ? "jungle" : m < T.aridT ? "desert" : "savanna";
  return wet ? "forest" : "grass";
}

/** Remove isolated 1-hex speckles via majority neighbor filter. */
function smoothBiomes(hexes: Hex[], passes: number) {
  const byAxial = new Map<string, Hex>();
  for (const h of hexes) byAxial.set(h.q + "," + h.r, h);
  for (let p = 0; p < passes; p++) {
    const flips: { h: Hex; biome: Biome; height: number }[] = [];
    for (const h of hexes) {
      const counts = new Map<Biome, number>();
      const hSum = new Map<Biome, number>();
      for (const [dq, dr] of HEX_DIRECTIONS) {
        const n = byAxial.get(h.q + dq + "," + (h.r + dr));
        if (!n) continue;
        counts.set(n.biome, (counts.get(n.biome) ?? 0) + 1);
        hSum.set(n.biome, (hSum.get(n.biome) ?? 0) + n.height);
      }
      if ((counts.get(h.biome) ?? 0) > 0) continue;
      let best: Biome | null = null;
      let bestN = 0;
      for (const [b, n] of counts) if (n > bestN) { bestN = n; best = b; }
      if (best && bestN >= 5) {
        flips.push({ h, biome: best, height: hSum.get(best)! / bestN });
      }
    }
    for (const f of flips) {
      f.h.biome = f.biome;
      f.h.height = f.height;
    }
  }
}

/** Laplacian height relaxation for smooth rolling hills. */
function smoothHeights(hexes: Hex[], passes: number) {
  const byAxial = new Map<string, Hex>();
  for (const h of hexes) byAxial.set(h.q + "," + h.r, h);
  const isWater = (b: Biome) => b === "water" || b === "deepWater";
  for (let p = 0; p < passes; p++) {
    const next = new Map<Hex, number>();
    for (const h of hexes) {
      if (isWater(h.biome)) continue;
      let sum = h.height;
      let n = 1;
      for (const [dq, dr] of HEX_DIRECTIONS) {
        const nb = byAxial.get(h.q + dq + "," + (h.r + dr));
        if (!nb || isWater(nb.biome)) continue;
        sum += nb.height;
        n++;
      }
      const avg = sum / n;
      next.set(h, h.height + (avg - h.height) * 0.65);
    }
    for (const [h, v] of next) h.height = v;
  }
}
